import type { ReadonlyData } from '@/modules/game-domain/render-snapshot';
import type { TownData } from '@/modules/game-domain/world.types';
import type { TownFortifications } from '@/modules/game-domain/authored-town.types';
import { worldToScreen } from './coordinates';
import { fortificationGeometry } from './fortification-geometry';
import { fortificationArtProfile, uniformGateMatrix, wallGateJoints } from './fortification-sprites';
import measuredProfiles from './remaining-fortification-profiles.json';

type Bounds = { left: number; right: number; top: number; bottom: number };
type Point = { x: number; y: number };
const materials = new Set(['stone', 'timber', 'palisade', 'swamp_stone', 'canyon_stone']);
const profiles = new Set(['town-v5', 'swamp-gate-v2', 'canyon-gate-v2', 'palisade-gate-v2',
  'meadow-gate-v2', 'capital-royal-v1', ...Object.keys(measuredProfiles)]);
// Saved geometry is immutable, matching FortificationSprites' existing cache contract.
// A replacement geometry gets a new entry. Unknown bounds stay conservative.
const cachedBounds = new WeakMap<ReadonlyData<TownFortifications>, Bounds | null>();

function measure(f: ReadonlyData<TownFortifications>): Bounds | null {
  if (!materials.has(f.material) || (f.artProfile !== undefined && !profiles.has(f.artProfile))
    || f.perimeter.length < 2 || !Number.isFinite(f.gateWidth) || f.gateWidth <= 0
    || [...f.perimeter, ...f.gates].some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;
  try {
    const art = fortificationArtProfile(f.material, f.artProfile);
    const geometry = fortificationGeometry(f);
    const result = { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity };
    const include = (p: Point) => {
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) throw new Error('Unknown fortification bounds');
      result.left = Math.min(result.left, p.x); result.right = Math.max(result.right, p.x);
      result.top = Math.min(result.top, p.y); result.bottom = Math.max(result.bottom, p.y);
    };
    for (const p of f.perimeter) include(worldToScreen(p.x, p.y));
    const wall = art.wall;
    if (wall.bodyHeight <= 0 || wall.moduleWidth <= 0 || wall.right.x === wall.left.x) return null;
    const slope = (wall.right.y - wall.left.y) / (wall.right.x - wall.left.x);
    // For every cropped/reversed module, its x stays on the section. Relative to
    // that section's projected baseline, y = scale*(pixelY-left.y-slope*u),
    // where 0<=pixelY<=height and 0<=u<=moduleWidth. The extra slope term is
    // conservative at partial ends. Splitting/masks/insets only remove pixels.
    const wallY = art.wallHeight / wall.bodyHeight
      * (wall.height + Math.abs(wall.left.y) + 2 * Math.abs(slope) * wall.moduleWidth);
    if (!Number.isFinite(wallY) || wallY < 0) return null;
    const includeWall = (a: Point, b: Point) => {
      include({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) - wallY });
      include({ x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) + wallY });
    };
    for (const section of geometry.walls) {
      const joined = wallGateJoints(section, geometry.gates, art.gate, art.gateHeight);
      includeWall(worldToScreen(joined.start.x, joined.start.y), worldToScreen(joined.end.x, joined.end.y));
    }
    const gate = art.gate;
    if (gate.nativeVerticalScaleReference <= 0 || gate.width <= 0 || gate.height <= 0) return null;
    for (const section of geometry.gates) {
      const a = worldToScreen(section.start.x, section.start.y), b = worldToScreen(section.end.x, section.end.y);
      const matrix = uniformGateMatrix(a, b, gate.left, gate.right,
        art.gateHeight / gate.nativeVerticalScaleReference, gate.placementAnchor);
      for (const x of [0, gate.width]) for (const y of [0, gate.height]) include(matrix.apply({ x, y }));
      if (Math.abs(b.x - a.x) < 1e-6) continue;
      // Gate return walls follow this exact baseline, including steep/short
      // sections; do not assume their contacts remain inside saved endpoints.
      const contacts = [matrix.apply(gate.left), matrix.apply(gate.right)].sort((p, q) => p.x - q.x);
      const ends = [a, b].sort((p, q) => p.x - q.x);
      for (let i = 0; i < 2; i++) {
        const end = ends[i]!, contact = contacts[i]!;
        const join = { x: contact.x, y: end.y + (b.y - a.y) / (b.x - a.x) * (contact.x - end.x) };
        includeWall(end, join);
      }
    }
    const tower = art.tower, scale = tower.displayHeight / tower.bodyHeight;
    if (tower.bodyHeight <= 0 || tower.width <= 0 || tower.height <= 0 || !Number.isFinite(scale) || scale <= 0) return null;
    // All candidate towers are a superset of visualFortificationTowers. Royal
    // foreground masks cannot extend the underlying full PNG rectangles.
    for (const p of geometry.towers) {
      const contact = worldToScreen(p.x, p.y);
      for (const x of [0, tower.width]) for (const y of [0, tower.height]) {
        include({ x: contact.x + (x - tower.ground.x) * scale, y: contact.y + (y - tower.ground.y) * scale });
      }
    }
    return result;
  } catch {
    // Unknown future art/calibration must load conservatively, never disappear.
    return null;
  }
}

/** Metadata only: no PNG lookup/decode, town radius, Sprite mutation or fixed padding. */
export function fortificationsIntersectViewport(town: ReadonlyData<TownData>, bounds: Bounds): boolean {
  const f = town.authored?.fortifications;
  if (!f) return false;
  if (!Object.values(bounds).every(Number.isFinite) || bounds.left > bounds.right || bounds.top > bounds.bottom) return true;
  if (!cachedBounds.has(f)) cachedBounds.set(f, measure(f));
  const measured = cachedBounds.get(f);
  return !measured || (measured.left <= bounds.right && measured.right >= bounds.left
    && measured.top <= bounds.bottom && measured.bottom >= bounds.top);
}
